import { API_BASE_URL } from '../config/api';

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   meetingDate: string,
 *   meetingEndDate: string,
 *   meetingRoomName: string | null,
 *   location: string | null,
 *   onlineMeetingUrl: string | null,
 *   description: string | null,
 *   mealPrice: number | null,
 *   currency: string | null,
 *   allowGuests: boolean,
 *   isActive: boolean,
 *   isCompleted: boolean,
 *   myAttendanceStatus: number | null,
 *   myAttendanceStatusName: string | null,
 *   myGuests: MeetingGuest[],
 * }} MeetingDetail
 */

/**
 * @typedef {{
 *   id: string,
 *   firstName: string,
 *   lastName: string,
 *   phoneNumber: string | null,
 *   fullName: string | null,
 * }} MeetingGuest
 */

/**
 * @typedef {Omit<MeetingGuest, 'id'>} MeetingGuestInput
 */

/** API: 4 = İptal Edildi */
export const MEETING_ATTENDANCE_STATUS_CANCELLED = 4;

/**
 * @param {Pick<MeetingDetail, 'myAttendanceStatus' | 'myAttendanceStatusName'> | null | undefined} detail
 */
export function isActiveMeetingRegistration(detail) {
    if (!detail) return false;

    if (typeof detail.myAttendanceStatus === 'number') {
        if (detail.myAttendanceStatus === MEETING_ATTENDANCE_STATUS_CANCELLED) {
            return false;
        }
        return detail.myAttendanceStatus > 0;
    }

    const name = detail.myAttendanceStatusName?.trim().toLowerCase() ?? '';
    if (!name) return false;
    if (name.includes('iptal') || name.includes('cancel')) return false;
    return true;
}

/**
 * @param {unknown} row
 * @returns {MeetingGuest | null}
 */
export function mapMeetingGuest(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = String(r.guestId ?? r.id ?? '').trim();
    if (!id) return null;

    let firstName = String(r.firstName ?? '').trim();
    let lastName = String(r.lastName ?? '').trim();
    const fullName = String(r.fullName ?? '').trim();

    if (!firstName && !lastName && fullName) {
        const parts = fullName.split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? '';
        lastName = parts.slice(1).join(' ');
    }

    if (!firstName && !lastName) return null;

    const phoneRaw = String(r.phoneNumber ?? r.phone ?? '').trim();

    return {
        id,
        firstName,
        lastName,
        phoneNumber: phoneRaw || null,
        fullName: fullName || null,
    };
}

/**
 * @param {MeetingGuestInput} guest
 * @returns {MeetingGuestInput | null}
 */
export function normalizeMeetingGuestInput(guest) {
    const firstName = String(guest.firstName || '').trim();
    const lastName = String(guest.lastName || '').trim();
    const phoneNumber = String(guest.phoneNumber || '').trim();
    if (!firstName || !lastName || !phoneNumber) return null;
    return { firstName, lastName, phoneNumber };
}

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<unknown>}
 */
async function parseDetailResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    return /** @type {{ data?: unknown }} */ (obj).data ?? null;
}

/**
 * @param {unknown} row
 * @returns {MeetingDetail | null}
 */
export function mapMeetingDetail(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = String(r.id ?? '').trim();
    if (!id) return null;

    const meetingDate = String(r.meetingDate ?? '');
    if (!meetingDate) return null;

    const meetingEndDate =
        r.meetingEndDate != null && String(r.meetingEndDate).trim()
            ? String(r.meetingEndDate)
            : meetingDate;

    return {
        id,
        title: String(r.title || 'Toplantı'),
        meetingDate,
        meetingEndDate,
        meetingRoomName:
            r.meetingRoomName != null && String(r.meetingRoomName).trim()
                ? String(r.meetingRoomName).trim()
                : null,
        location:
            r.location != null && String(r.location).trim()
                ? String(r.location).trim()
                : null,
        onlineMeetingUrl:
            r.onlineMeetingUrl != null &&
            String(r.onlineMeetingUrl).trim()
                ? String(r.onlineMeetingUrl).trim()
                : null,
        description:
            r.description != null && String(r.description).trim()
                ? String(r.description).trim()
                : null,
        mealPrice: typeof r.mealPrice === 'number' ? r.mealPrice : null,
        currency:
            r.currency != null && String(r.currency).trim()
                ? String(r.currency).trim()
                : null,
        allowGuests: r.allowGuests === true,
        isActive: r.isActive !== false,
        isCompleted: r.isCompleted === true,
        myAttendanceStatus:
            typeof r.myAttendanceStatus === 'number'
                ? r.myAttendanceStatus
                : null,
        myAttendanceStatusName:
            r.myAttendanceStatusName != null &&
            String(r.myAttendanceStatusName).trim()
                ? String(r.myAttendanceStatusName).trim()
                : null,
        myGuests: Array.isArray(r.myGuests)
            ? r.myGuests
                  .map(mapMeetingGuest)
                  .filter(
                      (/** @type {MeetingGuest | null} */ g) => g != null,
                  )
            : [],
    };
}

/**
 * @typedef {{
 *   firstName: string,
 *   lastName: string,
 *   phoneNumber: string,
 * }} MeetingRegisterGuest
 */

/**
 * @typedef {{
 *   meetingId: string,
 *   includeSubMember: boolean,
 *   guests?: MeetingRegisterGuest[],
 * }} MeetingRegisterPayload
 */

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<void>}
 */
async function parseMutationResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }
}

/**
 * `POST /meetings/api/Meeting/Register`
 * @param {string} accessToken
 * @param {MeetingRegisterPayload} payload
 */
export async function registerForMeeting(accessToken, payload) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const meetingId = String(payload.meetingId || '').trim();
    if (!meetingId) {
        throw new Error('Geçersiz toplantı');
    }

    /** @type {Record<string, unknown>} */
    const body = {
        meetingId,
        includeSubMember: Boolean(payload.includeSubMember),
    };

    const guests = Array.isArray(payload.guests)
        ? payload.guests
              .map((g) => ({
                  firstName: String(g.firstName || '').trim(),
                  lastName: String(g.lastName || '').trim(),
                  phoneNumber: String(g.phoneNumber || '').trim(),
              }))
              .filter(
                  (g) => g.firstName && g.lastName && g.phoneNumber,
              )
        : [];

    if (guests.length > 0) {
        body.guests = guests;
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/Register`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
    });

    await parseMutationResponse(res, 'Toplantı kaydı yapılamadı');
}

/**
 * `POST /meetings/api/Meeting/CancelRegistration`
 * @param {string} accessToken
 * @param {string} meetingId
 */
export async function cancelMeetingRegistration(accessToken, meetingId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    if (!id) {
        throw new Error('Geçersiz toplantı');
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/CancelRegistration`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ meetingId: id }),
    });

    await parseMutationResponse(res, 'Toplantı kaydı iptal edilemedi');
}

/**
 * `POST /meetings/api/Meeting/AddGuest`
 * @param {string} accessToken
 * @param {string} meetingId
 * @param {MeetingGuestInput} guest
 */
export async function addMeetingGuest(accessToken, meetingId, guest) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    if (!id) {
        throw new Error('Geçersiz toplantı');
    }
    const normalized = normalizeMeetingGuestInput(guest);
    if (!normalized) {
        throw new Error('Misafir bilgileri eksik');
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/AddGuest`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ meetingId: id, guest: normalized }),
    });

    await parseMutationResponse(res, 'Misafir eklenemedi');
}

/**
 * `POST /meetings/api/Meeting/RemoveGuest`
 * @param {string} accessToken
 * @param {string} meetingId
 * @param {string} guestId
 */
export async function removeMeetingGuest(accessToken, meetingId, guestId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    const gId = String(guestId || '').trim();
    if (!id || !gId) {
        throw new Error('Geçersiz istek');
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/RemoveGuest`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ meetingId: id, guestId: gId }),
    });

    await parseMutationResponse(res, 'Misafir kaldırılamadı');
}

/**
 * `PUT /meetings/api/Meeting/UpdateGuests`
 * @param {string} accessToken
 * @param {string} meetingId
 * @param {MeetingGuestInput[]} guests
 */
export async function updateMeetingGuests(accessToken, meetingId, guests) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    if (!id) {
        throw new Error('Geçersiz toplantı');
    }

    const normalized = (Array.isArray(guests) ? guests : [])
        .map(normalizeMeetingGuestInput)
        .filter((/** @type {MeetingGuestInput | null} */ g) => g != null);

    const url = `${API_BASE_URL}/meetings/api/Meeting/UpdateGuests`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ meetingId: id, guests: normalized }),
    });

    await parseMutationResponse(res, 'Misafir listesi güncellenemedi');
}

/**
 * @typedef {{
 *   id: string,
 *   fullName: string,
 *   firstName: string,
 *   lastName: string,
 *   checkedInAt: string | null,
 * }} MeetingParticipantGuest
 */

/**
 * @typedef {{
 *   personId: string,
 *   type: number | null,
 *   typeName: string | null,
 *   fullName: string | null,
 *   profilePictureUrl: string | null,
 *   parentMemberId: string | null,
 *   parentFullName: string | null,
 *   checkedInAt: string | null,
 *   guests: MeetingParticipantGuest[],
 * }} MeetingParticipant
 */

/**
 * @typedef {{
 *   meetingId: string,
 *   meetingTitle: string | null,
 *   meetingDate: string | null,
 *   participants: MeetingParticipant[],
 * }} MeetingParticipantsList
 */

/**
 * @param {unknown} row
 * @returns {MeetingParticipantGuest | null}
 */
export function mapMeetingParticipantGuest(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = String(r.guestId ?? r.id ?? '').trim();
    if (!id) return null;

    let firstName = String(r.firstName ?? '').trim();
    let lastName = String(r.lastName ?? '').trim();
    const fullName = String(r.fullName ?? '').trim();

    if (!firstName && !lastName && fullName) {
        const parts = fullName.split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? '';
        lastName = parts.slice(1).join(' ');
    }

    return {
        id,
        firstName,
        lastName,
        fullName: fullName || `${firstName} ${lastName}`.trim(),
        checkedInAt:
            r.checkedInAt != null && String(r.checkedInAt).trim()
                ? String(r.checkedInAt)
                : null,
    };
}

/**
 * @param {unknown} row
 * @returns {MeetingParticipant | null}
 */
export function mapMeetingParticipant(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const personId = String(r.personId ?? '').trim();
    if (!personId) return null;

    return {
        personId,
        type: typeof r.type === 'number' ? r.type : null,
        typeName:
            r.typeName != null && String(r.typeName).trim()
                ? String(r.typeName).trim()
                : null,
        fullName:
            r.fullName != null && String(r.fullName).trim()
                ? String(r.fullName).trim()
                : null,
        profilePictureUrl:
            r.profilePictureUrl != null &&
            String(r.profilePictureUrl).trim()
                ? String(r.profilePictureUrl).trim()
                : null,
        parentMemberId:
            r.parentMemberId != null &&
            String(r.parentMemberId).trim()
                ? String(r.parentMemberId).trim()
                : null,
        parentFullName:
            r.parentFullName != null &&
            String(r.parentFullName).trim()
                ? String(r.parentFullName).trim()
                : null,
        checkedInAt:
            r.checkedInAt != null && String(r.checkedInAt).trim()
                ? String(r.checkedInAt)
                : null,
        guests: Array.isArray(r.guests)
            ? r.guests
                  .map(mapMeetingParticipantGuest)
                  .filter(
                      (
                          /** @type {MeetingParticipantGuest | null} */ g,
                      ) => g != null,
                  )
            : [],
    };
}

/**
 * @param {unknown} data
 * @returns {MeetingParticipantsList}
 */
export function mapMeetingParticipantsList(data) {
    if (!data || typeof data !== 'object') {
        return {
            meetingId: '',
            meetingTitle: null,
            meetingDate: null,
            participants: [],
        };
    }

    const r = /** @type {Record<string, unknown>} */ (data);
    return {
        meetingId: String(r.meetingId ?? '').trim(),
        meetingTitle:
            r.meetingTitle != null && String(r.meetingTitle).trim()
                ? String(r.meetingTitle).trim()
                : null,
        meetingDate:
            r.meetingDate != null && String(r.meetingDate).trim()
                ? String(r.meetingDate)
                : null,
        participants: Array.isArray(r.participants)
            ? r.participants
                  .map(mapMeetingParticipant)
                  .filter(
                      (/** @type {MeetingParticipant | null} */ p) =>
                          p != null,
                  )
            : [],
    };
}

/**
 * `GET /meetings/api/Meeting/{meetingId}/Participants`
 * @param {string} accessToken
 * @param {string} meetingId
 * @returns {Promise<MeetingParticipantsList>}
 */
export async function fetchMeetingParticipants(accessToken, meetingId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    if (!id) {
        throw new Error('Geçersiz toplantı');
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/${encodeURIComponent(id)}/Participants`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const data = await parseDetailResponse(
        res,
        'Katılımcı listesi alınamadı',
    );
    return mapMeetingParticipantsList(data);
}

/**
 * `GET /meetings/api/Meeting/{id}`
 * @param {string} accessToken
 * @param {string} meetingId
 * @returns {Promise<MeetingDetail>}
 */
export async function fetchMeetingById(accessToken, meetingId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(meetingId || '').trim();
    if (!id) {
        throw new Error('Geçersiz toplantı');
    }

    const url = `${API_BASE_URL}/meetings/api/Meeting/${encodeURIComponent(id)}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const data = await parseDetailResponse(res, 'Toplantı detayı alınamadı');
    const mapped = mapMeetingDetail(data);
    if (!mapped) {
        throw new Error('Toplantı detayı okunamadı');
    }
    return mapped;
}
